import React from "react";
import { Select } from "antd";
import { ConfigProvider, DatePicker, Tooltip } from "antd";
import { SwapOutlined } from "@ant-design/icons";
import he_IL from "antd/locale/he_IL";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { SearchEngineField } from "../../../SearchEngine/SearchEngineShell";
import DestinationInput from "../SubComponents/DestinationInput/DestinationInput";
import DatesInput from "../SubComponents/DatesInput/DatesInput";
import PeopleCounterInput from "../SubComponents/PeopleCounterInput/PeopleCounterInput";
import { PackageDatesFieldContent } from "../PackageSearchBars/PackageDatesFieldContent";
import {
  getAllDepartureDates,
  getReturnDatesForDeparture,
  getAvailableMonthOfDeparture,
  getFallbackLdsDatesForAllDestinations,
  resolveLdsSearchCityFromLocation,
} from "../../../../contexts/SearchContextHelpers";
import { Loader } from "../../../Loader/Loader";
import {
  CAMPAIGNS,
  DYNAMIC_PACKAGES,
  HOTELS,
  ORGANIZED_TOURS,
  PACKAGES,
  PLANES,
  useSearch,
} from "../../../../contexts/SearchContext";
import { HOTEL_SEARCH_CURRENCIES } from "../../../../constants/hotelCurrencies";
import { getPackageCategoryDisplayLabel } from "../../../../constants/packageQuickCategoryItems";
import { dateHebLocale } from "../OrganizedTourSearchBar/organizedTourSearchBar";
import {
  buildCampaignDestinationSelectOptions,
  resolveCampaignDestinationCodeFromLocation,
} from "../../../../utils/searchCampaignHotels";
import "./SearchEngineTabFields.css";

/** רוחב שדות: סכום span חייב להיות 10 (אזור השדות); המעטפת מוסיפה עמודת כפתור חיפוש קבועה. */

function categorySelectOptions(subjectsList, allLabel) {
  return (subjectsList || []).map((category) => ({
    value: category,
    label: getPackageCategoryDisplayLabel(category, allLabel),
  }));
}

const categorySelectPopupProps = {
  popupMatchSelectWidth: false,
  listHeight: 360,
  classNames: { popup: { root: "category-select-dropdown" } },
  dropdownStyle: { minWidth: 240 },
};

function PlaneFields({ wideOriginDestination }) {
  const {
    calendarDateArrival,
    setCalendarDateArrival,
    calendarDateLeave,
    setCalendarDateLeave,
    roomInfo,
    setRoomInfo,
    origin,
    setOrigin,
    location,
    setLocation,
    flightTripType,
  } = useSearch();

  const odSpan = wideOriginDestination ? 2 : 2;
  const secondarySpan = wideOriginDestination ? 3 : 3;

  const hasOrigin = Boolean(origin);
  const hasDestination = Boolean(location);
  const canSwapLocations = hasOrigin && hasDestination;

  const onSwapLocations = () => {
    if (!canSwapLocations) return;
    const beforeOrigin = origin ?? null;
    const beforeDestination = location ?? null;
    const afterOrigin = beforeDestination;
    const afterDestination = beforeOrigin;

    setOrigin(afterOrigin);
    setLocation(afterDestination, { preserveSearchState: true });

    if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
      console.log("[FLIGHT_SEARCH_SWAP_LOCATIONS]", {
        beforeOrigin,
        beforeDestination,
        afterOrigin,
        afterDestination,
      });
    }
  };

  return (
    <>
      <SearchEngineField span={odSpan}>
        <DestinationInput
          location={origin}
          setLocation={setOrigin}
          isLdsInput
          isOrigin
        />
      </SearchEngineField>
      <SearchEngineField
        span={1}
        className="search-engine-shell__field--swap-control"
      >
        <div className="flight-search-swap-wrap">
          <button
            type="button"
            className="flight-search-swap-btn"
            onClick={onSwapLocations}
            disabled={!canSwapLocations}
            aria-label="החלף בין מוצא ליעד"
            title="החלף בין מוצא ליעד"
          >
            <SwapOutlined />
          </button>
        </div>
      </SearchEngineField>
      <SearchEngineField span={odSpan}>
        <DestinationInput
          location={location}
          setLocation={setLocation}
          isLdsInput
        />
      </SearchEngineField>
      <SearchEngineField span={secondarySpan}>
        <DatesInput
          calendarDateArrival={calendarDateArrival}
          calendarDateLeave={calendarDateLeave}
          setCalendarDateArrival={setCalendarDateArrival}
          setCalendarDateLeave={setCalendarDateLeave}
          tripType={flightTripType}
          unrestrictedReturnDates
        />
      </SearchEngineField>
      <SearchEngineField span={2}>
        <PeopleCounterInput
          roomInfo={roomInfo}
          setRoomInfo={setRoomInfo}
          productType="flight"
        />
      </SearchEngineField>
    </>
  );
}

function HotelFields() {
  const {
    calendarDateArrival,
    setCalendarDateArrival,
    calendarDateLeave,
    setCalendarDateLeave,
    roomInfo,
    setRoomInfo,
    location,
    setLocation,
    selectedHotelCurrency,
    setSelectedHotelCurrency,
  } = useSearch();

  return (
    <>
      <SearchEngineField span={4} className="search-engine-shell__field--primary-destination">
        <DestinationInput location={location} setLocation={setLocation} />
      </SearchEngineField>
      <SearchEngineField span={3} className="search-engine-shell__field--secondary-dates">
        <DatesInput
          calendarDateArrival={calendarDateArrival}
          calendarDateLeave={calendarDateLeave}
          setCalendarDateArrival={setCalendarDateArrival}
          setCalendarDateLeave={setCalendarDateLeave}
        />
      </SearchEngineField>
      <SearchEngineField span={2} className="search-engine-shell__field--passengers">
        <PeopleCounterInput
          roomInfo={roomInfo}
          setRoomInfo={setRoomInfo}
          productType="hotel"
        />
      </SearchEngineField>
      <SearchEngineField span={1} className="search-engine-shell__field--hotel-currency">
        <div
          className="hotel-search-currency-field"
          title="מטבע לחיפוש הבא. כשכבר יש תוצאות מלונות — מתבצע חיפוש מחדש אוטומטי (מלונות / חבילה דינמית)."
        >
          <Select
            value={selectedHotelCurrency}
            options={HOTEL_SEARCH_CURRENCIES.map((c) => ({
              value: c.code,
              label: `${c.symbol} ${c.code}`,
            }))}
            onChange={setSelectedHotelCurrency}
            className="hotel-search-currency-select"
            getPopupContainer={() => document.body}
            aria-label="מטבע חיפוש מלונות"
          />
        </div>
      </SearchEngineField>
    </>
  );
}

function CampaignDestinationSelect() {
  const { location, setLocation } = useSearch();
  const options = buildCampaignDestinationSelectOptions();
  const value = resolveCampaignDestinationCodeFromLocation(
    Array.isArray(location) ? location[0] : location,
  );

  return (
    <Select
      value={value}
      options={options}
      placeholder="בחרו יעד קמפיין"
      onChange={(code) => {
        const row = options.find((opt) => opt.value === code);
        setLocation(row?.location || "");
      }}
      allowClear
      onClear={() => setLocation("")}
      className="campaign-destination-select"
      getPopupContainer={() => document.body}
      aria-label="יעד קמפיין"
      optionFilterProp="label"
      showSearch
    />
  );
}

function CampaignFields() {
  const {
    calendarDateArrival,
    setCalendarDateArrival,
    calendarDateLeave,
    setCalendarDateLeave,
    roomInfo,
    setRoomInfo,
  } = useSearch();

  return (
    <>
      <SearchEngineField span={4} className="search-engine-shell__field--primary-destination">
        <CampaignDestinationSelect />
      </SearchEngineField>
      <SearchEngineField span={3} className="search-engine-shell__field--secondary-dates">
        <DatesInput
          calendarDateArrival={calendarDateArrival}
          calendarDateLeave={calendarDateLeave}
          setCalendarDateArrival={setCalendarDateArrival}
          setCalendarDateLeave={setCalendarDateLeave}
        />
      </SearchEngineField>
      <SearchEngineField span={3} className="search-engine-shell__field--passengers">
        <PeopleCounterInput
          roomInfo={roomInfo}
          setRoomInfo={setRoomInfo}
          productType="hotel"
        />
      </SearchEngineField>
    </>
  );
}

function PackageFields() {
  const { t } = useTranslation();
  const { roomInfo, setRoomInfo, location, setLocation, categories, setCategories, subjects } =
    useSearch();

  return (
    <>
      <SearchEngineField span={4} className="search-engine-shell__field--primary-destination">
        <DestinationInput
          location={location}
          setLocation={setLocation}
          isLdsInput
        />
      </SearchEngineField>
      <SearchEngineField span={3} className="search-engine-shell__field--secondary-dates">
        <PackageDatesFieldContent />
      </SearchEngineField>
      <SearchEngineField span={2} className="search-engine-shell__field--passengers">
        <PeopleCounterInput
          roomInfo={roomInfo}
          setRoomInfo={setRoomInfo}
          productType="package"
        />
      </SearchEngineField>
      <SearchEngineField span={1} className="search-engine-shell__field--category-secondary">
        <Select
          placeholder={t("hotelsearchbar.categoryType")}
          value={categories?.[0]}
          options={categorySelectOptions(
            subjects?.FlightsHotel,
            t("hotelsearchbar.allCategories")
          )}
          onChange={(value) => setCategories([value])}
          className="category-select-field"
          getPopupContainer={() => document.body}
          aria-label={t("hotelsearchbar.categoryType")}
          {...categorySelectPopupProps}
        />
      </SearchEngineField>
    </>
  );
}

function OrganizedTourMonthInner() {
  const {
    setTourMonth,
    location,
    ldsDates,
    datesError,
    loadingDates,
    tourMonth,
  } = useSearch();
  const [monthPickerOpen, setMonthPickerOpen] = React.useState(false);
  const resolvedForOrgDates = resolveLdsSearchCityFromLocation(location);
  const isAllDestinations = resolvedForOrgDates.isAllDestinations;

  React.useEffect(() => {
    if (!import.meta.env?.DEV) return;
    const apiMc =
      Array.isArray(ldsDates) && ldsDates.length > 0
        ? getAvailableMonthOfDeparture(ldsDates).length
        : 0;
    const usesFallbackApprox =
      isAllDestinations &&
      (loadingDates ||
        !Array.isArray(ldsDates) ||
        !ldsDates?.length ||
        apiMc === 0);
    console.log("[ORG_TOUR_DATES_UI_DISABLED_REASON]", {
      resolvedCity: resolvedForOrgDates.city,
      isAllDestinations,
      loadingDates,
      parsedApiMonths: apiMc,
      usesFallbackApprox,
      ldsDatesLen: Array.isArray(ldsDates) ? ldsDates.length : null,
    });
  }, [
    resolvedForOrgDates.city,
    isAllDestinations,
    loadingDates,
    ldsDates,
  ]);

  const hasDestination =
    isAllDestinations ||
    Boolean(
      location &&
        (Array.isArray(location) ? location.length > 0 : true) &&
        !(typeof location === "string" && location.trim() === ""),
    );

  const parsedApiMonthCount =
    Array.isArray(ldsDates) && ldsDates.length > 0
      ? getAvailableMonthOfDeparture(ldsDates).length
      : 0;

  const effectiveLdsDates =
    isAllDestinations &&
    (loadingDates ||
      !Array.isArray(ldsDates) ||
      ldsDates.length === 0 ||
      parsedApiMonthCount === 0)
      ? getFallbackLdsDatesForAllDestinations()
      : ldsDates;

  if (datesError) {
    return (
      <span className="search-engine-shell__control-placeholder">{datesError}</span>
    );
  }
  if (!hasDestination && !effectiveLdsDates?.length) {
    return (
      <span className="search-engine-shell__control-placeholder">יש לבחור יעד</span>
    );
  }
  if (loadingDates && !isAllDestinations) {
    return (
      <span className="search-engine-shell__control-placeholder search-engine-shell__control-placeholder--relative">
        <Loader size="small" inline />
      </span>
    );
  }
  const availableMonths = getAvailableMonthOfDeparture(effectiveLdsDates);
  const disabledDate = (current) =>
    current && availableMonths.indexOf(current.format("MM/YYYY")) === -1;

  const tooltipTitle = !hasDestination
    ? "בחר תאריכים"
    : !loadingDates && availableMonths.length === 0
      ? "אין תאריכים זמינים"
      : null;

  const monthPicker = (
    <ConfigProvider locale={he_IL}>
          <DatePicker.MonthPicker
            format="MM/YYYY"
            value={tourMonth ? dayjs(tourMonth, "MM/YYYY") : null}
            open={monthPickerOpen}
            onOpenChange={setMonthPickerOpen}
            disabledDate={disabledDate}
            disabled={!hasDestination || (!loadingDates && availableMonths.length === 0)}
            onPanelChange={(value) => {
              if (value) {
                const formatted = value.format("MM/YYYY");
                const isAvailable = availableMonths.indexOf(formatted) !== -1;
                const isNewSelection = formatted !== (tourMonth || "");
                if (isAvailable && isNewSelection) {
                  setTourMonth(formatted);
                  setMonthPickerOpen(false);
                }
              }
            }}
            onChange={(date, dateString) => {
              const toSet =
                (dateString && dateString.trim()) !== ""
                  ? dateString
                  : date
                    ? date.format("MM/YYYY")
                    : "";
              if (toSet) setTourMonth(toSet);
              setMonthPickerOpen(false);
            }}
            locale={dateHebLocale}
          />
    </ConfigProvider>
  );

  return (
    <div className="date-picker-field organized-tour-month-field">
      {tooltipTitle ? (
        <Tooltip title={tooltipTitle}>
          <span className="organized-tour-month-picker-trigger">{monthPicker}</span>
        </Tooltip>
      ) : (
        monthPicker
      )}
    </div>
  );
}

function OrganizedFields() {
  const { t } = useTranslation();
  const { roomInfo, setRoomInfo, location, setLocation, categories, setCategories, subjects } =
    useSearch();

  return (
    <>
      <SearchEngineField span={4} className="search-engine-shell__field--primary-destination">
        <DestinationInput
          location={location}
          setLocation={setLocation}
          isLdsInput
          isOrganizedTour={true}
        />
      </SearchEngineField>
      <SearchEngineField span={3} className="search-engine-shell__field--secondary-dates">
        <OrganizedTourMonthInner />
      </SearchEngineField>
      <SearchEngineField span={2} className="search-engine-shell__field--passengers">
        <PeopleCounterInput
          roomInfo={roomInfo}
          setRoomInfo={setRoomInfo}
          isTour={true}
          productType="package"
        />
      </SearchEngineField>
      <SearchEngineField span={1} className="search-engine-shell__field--category-secondary">
        <Select
          placeholder={t("hotelsearchbar.categoryType")}
          value={categories?.[0]}
          options={categorySelectOptions(
            subjects?.OrganizedTours,
            t("hotelsearchbar.allCategories")
          )}
          onChange={(value) => setCategories([value])}
          className="category-select-field"
          getPopupContainer={() => document.body}
          aria-label={t("hotelsearchbar.categoryType")}
          {...categorySelectPopupProps}
        />
      </SearchEngineField>
    </>
  );
}

function DynamicFields() {
  const {
    calendarDateArrival,
    setCalendarDateArrival,
    calendarDateLeave,
    setCalendarDateLeave,
    roomInfo,
    setRoomInfo,
    origin,
    setOrigin,
    location,
    setLocation,
    ldsDates,
    datesError,
    loadingDates,
  } = useSearch();

  let datesSlot;
  if (datesError) {
    datesSlot = (
      <span className="search-engine-shell__control-placeholder">{datesError}</span>
    );
  } else if ((!location || location.length === 0) && !ldsDates) {
    datesSlot = (
      <span className="search-engine-shell__control-placeholder">יש לבחור יעד</span>
    );
  } else if (loadingDates) {
    datesSlot = (
      <span className="search-engine-shell__control-placeholder search-engine-shell__control-placeholder--relative">
        <Loader size="small" inline />
      </span>
    );
  } else {
    datesSlot = (
      <DatesInput
        calendarDateArrival={calendarDateArrival}
        calendarDateLeave={calendarDateLeave}
        setCalendarDateArrival={setCalendarDateArrival}
        setCalendarDateLeave={setCalendarDateLeave}
        possibleArrivalDates={getAllDepartureDates(ldsDates)}
        possibleDepartureDates={getReturnDatesForDeparture(
          ldsDates,
          calendarDateArrival
        )}
        ldsDates={ldsDates}
        tripType="RoundTrip"
        unrestrictedReturnDates
      />
    );
  }

  return (
    <>
      <SearchEngineField span={2}>
        <DestinationInput
          location={origin}
          setLocation={(loc) => {
            console.log("origin", loc);
            setOrigin(loc);
          }}
          isLdsInput
          isOrigin
        />
      </SearchEngineField>
      <SearchEngineField span={2}>
        <DestinationInput
          location={location}
          setLocation={(loc) => setLocation(loc)}
          isLdsInput
        />
      </SearchEngineField>
      <SearchEngineField span={3}>{datesSlot}</SearchEngineField>
      <SearchEngineField span={3}>
        <PeopleCounterInput
          roomInfo={roomInfo}
          setRoomInfo={setRoomInfo}
          productType="package"
        />
      </SearchEngineField>
    </>
  );
}

export default function SearchEngineTabFields({ searchTab, planeFieldsWideOriginDestination }) {
  switch (searchTab) {
    case PLANES:
      return <PlaneFields wideOriginDestination={Boolean(planeFieldsWideOriginDestination)} />;
    case HOTELS:
      return <HotelFields />;
    case PACKAGES:
      return <PackageFields />;
    case CAMPAIGNS:
      return <CampaignFields />;
    case ORGANIZED_TOURS:
      return <OrganizedFields />;
    case DYNAMIC_PACKAGES:
      return <DynamicFields />;
    default:
      return null;
  }
}
